import { z } from "zod";
import { IsoDateTime, OperatorEnvironment, PlayerRef } from "../common.ts";
import { Cursor } from "./common.ts";

// Internal notes of a user profile (spec explorer-user-profile "[FR] Internal note", design D4):
// `GET /users/{ref}/notes` (`explorer.read`) and `POST /users/{ref}/notes` (`notes.write`, audited
// `note_added`). A note has 1-2,000 characters, an author and a date, and is never edited.

/** Longest note (characters); checked by the domain, which answers `invalid_request` + reason. */
export const NOTE_MAX_LENGTH = 2000;
export const DEFAULT_NOTES_LIMIT = 50;
export const MAX_NOTES_LIMIT = 100;

export const NotesQueryV1 = z
  .object({
    /** The operator of the profile (selector); default: the operator where the player was seen last. */
    operator_id: z.uuid().optional(),
    cursor: Cursor.optional(),
    limit: z.coerce.number().int().min(1).max(MAX_NOTES_LIMIT).default(DEFAULT_NOTES_LIMIT),
  })
  .strict()
  .meta({ id: "NotesQueryV1" });
export type NotesQueryV1 = z.infer<typeof NotesQueryV1>;

/**
 * Body of `POST /users/{ref}/notes`. The size guard here is generous (UTF-16 units); the domain
 * rule (1-2,000 characters after trimming) answers `note_empty` / `note_too_long`.
 */
export const CreateNoteRequestV1 = z
  .object({
    operator_id: z.uuid(),
    text: z.string().max(NOTE_MAX_LENGTH * 2),
  })
  .strict()
  .meta({ id: "CreateNoteRequestV1" });
export type CreateNoteRequestV1 = z.infer<typeof CreateNoteRequestV1>;

export const NoteV1 = z
  .object({
    note_id: z.uuid(),
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    player_ref: PlayerRef,
    text: z
      .string()
      .min(1)
      .max(NOTE_MAX_LENGTH * 2),
    /** Console user id of the author (iam); the Explorer shows the author's name from iam. */
    author_id: z.uuid(),
    created_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "NoteV1" });
export type NoteV1 = z.infer<typeof NoteV1>;

export const NotesResponseV1 = z
  .object({
    operator_id: z.uuid(),
    player_ref: PlayerRef,
    /** Newest first. */
    items: z.array(NoteV1),
    next_cursor: Cursor.nullable(),
  })
  .strict()
  .meta({ id: "NotesResponseV1" });
export type NotesResponseV1 = z.infer<typeof NotesResponseV1>;
